// Hosts that serve the citizen-facing report page. Keep in sync with
// `getServicesBaseHost` in src/components/online-reporting/citizen-engage/utils.ts —
// that helper produces `(www|test).ourcommunitysafety.(ca|com)` based on the
// org's api_region and environment. The legacy `services.tryrubicon.com` pair
// stays in the list because older outbound messages still reference it.
const RUBICON_HOSTS = new Set<string>([
  "services.tryrubicon.com",
  "test.services.tryrubicon.com",
  "www.ourcommunitysafety.ca",
  "test.ourcommunitysafety.ca",
  "www.ourcommunitysafety.com",
  "test.ourcommunitysafety.com",
  "localhost",
]);

const REPORT_PATH_PATTERN = /\/online-reports\/report\/[^/]+/;
const URL_PATTERN = /https?:\/\/\S+/g;

function isUntaggedRubiconReportUrl(rawUrl: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return false;
  }
  if (!RUBICON_HOSTS.has(parsed.hostname)) return false;
  if (!REPORT_PATH_PATTERN.test(parsed.pathname)) return false;
  if (parsed.searchParams.has("lang")) return false;
  return true;
}

export type RubiconReportUrlMatch = {
  /** The exact substring as it appears in the source text (may end with
   * trailing punctuation like `.` or `,`). Use this as the search needle when
   * rewriting the original text so the replacement actually lands. */
  match: string;
  /** The URL with trailing punctuation stripped — parseable, suitable for
   * `appendLangParam` and for display. */
  url: string;
};

export function findRubiconReportUrls(text: string): RubiconReportUrlMatch[] {
  const matches = text.match(URL_PATTERN) ?? [];
  return matches
    .map((match) => ({ match, url: match.replace(/[.,!?)>"]+$/, "") }))
    .filter(({ url }) => isUntaggedRubiconReportUrl(url));
}

/**
 * Map portal-emitted lang codes to the set services-frontend's i18n loader
 * accepts. Returns null when the code has no services-frontend equivalent —
 * callers treat that as "do not tag the URL" and let the citizen's stored or
 * browser locale take over.
 *
 * The portal's RubiMESSAGE picker (common/constants/languages.ts) and the
 * services-frontend's locale list have drifted. This bridge ships in both
 * `detectReportUrl.ts` (used by ChatView's custom-message rewrite) and the
 * Rust handler (used by managed-template send) — keep them in sync.
 */
export function normalizeLangForReferral(lang: string): string | null {
  const trimmed = lang.trim();
  switch (trimmed) {
    // Chinese: services-frontend has only one Chinese locale (`zh`).
    case "zh-CN":
    case "zh-TW":
      return "zh";
    // Tagalog `tl` ≈ Filipino `fil` (services-frontend uses the newer code).
    case "tl":
      return "fil";
    // Tamil (`ta`) now has a services-frontend locale, so it passes through via
    // the default branch below.
    case "":
      return null;
    default:
      return trimmed;
  }
}

export function appendLangParam(rawUrl: string, lang: string): string {
  const normalized = normalizeLangForReferral(lang);
  if (normalized === null) return rawUrl;
  const parsed = new URL(rawUrl);
  parsed.searchParams.set("lang", normalized);
  return parsed.toString();
}
